import { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Button, useTheme, Chip } from '@mui/material';
import { BaseRepository } from '../../repositories/base_repository/base_repository';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useReactToPrint } from 'react-to-print';
import BrandingHeader from './Templates/components/BrandingHeader';

import StatementTemplateRenderer from './StatementTemplateRenderer';
import AppealStatementModal from './AppealStatementModal';
import UploadInvoiceModal from './UploadInvoiceModal';

export default function StatementDetails({ statement, handleViewStatementClick, siteNames }) {
  const theme = useTheme();
  const [showAppealStatementModal, setAppealStatementModal] = useState(false);
  const [showUploadInvoiceModal, setUploadInvoiceModal] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [hasBeenAppealed, setHasBeenAppealed] = useState(false);

  const statementContainerRef = useRef(null);
  const printFn = useReactToPrint({
    contentRef: statementContainerRef,
    documentTitle: statement.statementRef,
  });

  const handleAppealClick = () => setAppealStatementModal(true);
  const handleUploadInvoiceClick = () => setUploadInvoiceModal(true);

  const onCloseAppealStatementModal = () => setAppealStatementModal(false);
  const onCloseUploadInvoiceModal = () => setUploadInvoiceModal(false);

  const user = useSelector((state) => state.user.user);

  const onSendAppeal = async (appealMsg) => {
    setLoading(true);

    const statementref = statement.statementRef;
    const emailFrom = user.email;
    const token = user.token;

    try {
      await BaseRepository.sendStatementAppeal(emailFrom, appealMsg, statementref, token);
      toast.success('Statement appealed successfully');
    } catch (error) {
      toast.error('Failed to appeal statement. Please try again or contact support');
    } finally {
      setAppealStatementModal(false);
      setLoading(false);
      setHasBeenAppealed(true)
    }
  };

  return (
    <div>
      <div className='flex justify-between space-y-2 pb-4'>
        <Button
          sx={{
            fontSize: '18px',
            color: theme.palette.btnActiveColor,
            textTransform: 'none',
            ml: 1,
            '&.MuiButtonBase-root:hover': {
              bgcolor: 'transparent',
            },
          }}
          variant="text"
          size="large"
          onClick={() => handleViewStatementClick(null)}
        >
          {'< Back'}
        </Button>

        <Button
          sx={{
            color: theme.palette.btnActiveColor,
            textTransform: 'none',
            // ml: 1,
            border: '1px solid',
            borderColor: theme.palette.btnActiveColor,
            borderRadius: '50px',
            '&.MuiButtonBase-root:hover': {
              bgcolor: 'transparent',
            },
            px: 3,
          }}
          variant="text"
          onClick={printFn}
        >
          Print/Save PDF
        </Button>
      </div>

      <div ref={statementContainerRef} className="bg-white rounded-[12px] p-8">
        <BrandingHeader />
        <h2 className="text-[24px] font-medium mb-4">{statement.statementRef}</h2>
        <h3 className="text-[20px] font-medium mb-4">{siteNames[statement.siteId]}</h3>

        <div className="flex justify-between">
          <Chip label="Statement Frequency: Quarterly" variant="outlined" />
          <p className="text-[16px] font-small">{`Issue Date: ${moment(
            statement.issueDate.toDate(),
          ).format('DD-MM-YYYY hh:mm:ss A')}`}</p>
        </div>
        <div className="flex justify-end mb-8">
          <p className="text-[16px] font-small">
            {statement.lastUpdated
              ? `Last Updated: ${moment(statement.lastUpdated.toDate()).format(
                  'DD-MM-YYYY hh:mm:ss A',
                )}`
              : 'Last Updated: -'}
          </p>
        </div>

        <div>
          <StatementTemplateRenderer statement={statement} />
        </div>
        
      </div>
      <hr className="mt-3 mb-3" />
      <div className="md:flex jusitfy-between space-y-2 pt-6 pb-4 mb-6">
        <p className="md:w-1/2 font-medium text-sm self-center">
          { statement.isAppealed || hasBeenAppealed ? 
            'Your statement has already been appealed and is currently under review by the team at Roam. We will respond to you as soon as possible.' : 
            'This statement was created using data in accordance with your contract and any updated information you may have provided. If any of the figures are believed to be incorrect, please appeal your statement.'}
        </p>
        <div className="md:w-1/2 text-right self-center">
          <Button
            sx={{
              color: theme.palette.btnActiveColor,
              textTransform: 'none',
              ml: 1,
              border: '1px solid',
              borderColor: theme.palette.btnActiveColor,
              borderRadius: '20px',
              '&.MuiButtonBase-root:hover': {
                bgcolor: 'transparent',
              },
              px: 4,
            }}
            variant="text"
            onClick={handleAppealClick}
            disabled={statement.isAppealed || hasBeenAppealed}
          >
            Appeal statement
          </Button>
        </div>
      </div>
      <hr className="mt-3 mb-3" />
      <div className="md:flex justify-between space-y-2 pt-8 pb-4 mb-6">
        <div>
          <p className=" font-medium text-sm">
            To settle any payments due, please upload an invoice to:
          </p>
          <br/>
          <p className='md:w-1/2 font-medium text-sm'>
            EV Chargers Ltd <br/>
            Nexus House <br/>
            4 Gatwick Rd <br/>
            Crawley <br/>
            RH10 9BG <br/>
            United Kingdom<br/>
          </p>
        </div>
        <div className="md:w-1/2 self-end text-right">
          {!statement.paid && (
            <Button
              sx={{
                color: 'white',
                backgroundColor: theme.palette.btnActiveColor,
                boxShadow: 'none',
                borderRadius: '25px',
                textTransform: 'none',
                ml: 1,
                '&.MuiButtonBase-root:hover': {
                  backgroundColor: theme.palette.btnActiveColor,
                  filter: 'brightness(0.9)',
                },
                px: 4,
              }}
              variant="contained"
              onClick={handleUploadInvoiceClick}
            >
              Upload Invoice
            </Button>
          )}
        </div>
      </div>
      <hr className="mt-3 mb-3" />

      <AppealStatementModal
        isOpen={showAppealStatementModal}
        onCloseModal={onCloseAppealStatementModal}
        isLoading={isLoading}
        onSendAppeal={onSendAppeal}
      />
      <UploadInvoiceModal
        isOpen={showUploadInvoiceModal}
        onCloseModal={onCloseUploadInvoiceModal}
        isLoading={isLoading}
        statementRef={statement.statementRef}
      />
    </div>
  );
}
StatementDetails.propTypes = {
  statement: PropTypes.object.isRequired,
  handleViewStatementClick: PropTypes.func.isRequired,
  siteNames: PropTypes.object.isRequired,
};
